import { useState, useEffect } from "react";
import ReactGA from "react-ga4";
import emailjs from "@emailjs/browser";
import navIcon1 from "../assets/img/nav-icon1.svg";
import navIcon2 from "../assets/img/nav-icon2.svg";
import github from "../assets/img/github.svg";
import { DownloadConfirmationModal } from "./DownloadConfirmationModal";
import Config from "../config/index";
import "../assets/styles/leftNavBar.css";

const sections = [
    { id: "home", label: "Home", number: "00" },
    { id: "skills", label: "Skills", number: "01" },
    { id: "timeline", label: "Career", number: "02" },
    { id: "projects", label: "Projects", number: "03" },
    { id: "connect", label: "Contact", number: "04" },
];

export const LeftNavBar = () => {
    const [activeSection, setActiveSection] = useState("home");
    const [showModal, setShowModal] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY + window.innerHeight * 0.35;
            let current = sections[0].id;
            for (const s of sections) {
                const el = document.getElementById(s.id);
                if (el && el.offsetTop <= scrollY) {
                    current = s.id;
                }
            }
            setActiveSection(current);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const scrollToSection = (sectionId) => {
        const el = document.getElementById(sectionId);
        if (el) {
            el.scrollIntoView({ behavior: "smooth" });
        }
        setMenuOpen(false);
        ReactGA.event({
            category: "Navigation",
            action: `Clicked on ${sectionId}`,
            label: sectionId,
        });
    };

    const handleDownloadCV = (e) => {
        e.preventDefault();
        setShowModal(true);
        setMenuOpen(false);
    };

    const handleConfirmDownload = (name) => {
        const serviceId = `${Config.REACT_APP_EMAILJS_SERVICE_ID}`;
        const templateId = `${Config.REACT_APP_EMAILJS_TEMPLATE_ID}`;
        const publicKey = `${Config.REACT_APP_EMAILJS_PUBLIC_KEY}`;

        const templateParams = {
            user_name: name,
            download_time: new Date().toLocaleString(),
            user_agent: navigator.userAgent,
            page_url: window.location.href,
        };

        emailjs.send(serviceId, templateId, templateParams, publicKey).then(
            (response) => console.log("Email sent successfully!", response.status, response.text),
            (error) => console.log("Email failed to send:", error)
        );

        ReactGA.event({
            category: "Downloads",
            action: "Downloaded CV",
            label: "CV Download",
        });

        const downloads = [
            { href: "/cv.pdf", name: "Ushan_Mihiranga_CV.pdf" },
            { href: "/Ushan_Mihiranga_Cover_Letter.pdf", name: "Ushan_Mihiranga_Cover_Letter.pdf" },
        ];

        downloads.forEach((file, index) => {
            const link = document.createElement("a");
            link.href = file.href;
            link.download = file.name;
            document.body.appendChild(link);
            setTimeout(() => {
                link.click();
                document.body.removeChild(link);
            }, index * 150);
        });
    };

    return (
        <>
            {/* ── Mobile hamburger ─────────────────────── */}
            <button
                className={`lnav-hamburger ${menuOpen ? "open" : ""}`}
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="Toggle navigation"
            >
                <span />
                <span />
                <span />
            </button>

            {/* ── Mobile overlay ───────────────────────── */}
            {menuOpen && (
                <div
                    className="lnav-overlay"
                    onClick={() => setMenuOpen(false)}
                />
            )}

            {/* ── Left Navbar ──────────────────────────── */}
            <nav className={`left-navbar ${menuOpen ? "mobile-open" : ""}`}>

                {/* Divider line + track */}
                <div className="lnav-track">
                    <div className="lnav-line" />

                    {sections.map((s) => (
                        <button
                            key={s.id}
                            className={`lnav-item ${activeSection === s.id ? "active" : ""}`}
                            onClick={() => scrollToSection(s.id)}
                            title={s.label}
                        >
                            {/* Number */}
                            <span className="lnav-number">{s.number}</span>

                            {/* Dot indicator */}
                            <span className="lnav-dot">
                                <span className="lnav-dot-inner" />
                            </span>

                            {/* Label (visible on expand / mobile) */}
                            <span className="lnav-label">{s.label}</span>
                        </button>
                    ))}
                </div>

                {/* Vertical brand text */}
                <div className="lnav-brand">
                    <span>USHAN MIHIRANGA</span>
                </div>

                {/* Footer: socials + CV */}
                <div className="lnav-footer">
                    <div className="lnav-footer-divider" />
                    <div className="lnav-socials">
                        <a
                            href="https://www.linkedin.com/in/ushan-mihiranga-b25846296"
                            target="_blank"
                            rel="noreferrer"
                            onClick={() =>
                                ReactGA.event({ category: "Social Click", action: "Clicked LinkedIn", label: "LinkedIn" })
                            }
                        >
                            <img src={navIcon1} alt="LinkedIn" />
                        </a>
                        <a
                            href="https://www.facebook.com/profile.php?id=100077429490019"
                            target="_blank"
                            rel="noreferrer"
                            onClick={() =>
                                ReactGA.event({ category: "Social Click", action: "Clicked Facebook", label: "Facebook" })
                            }
                        >
                            <img src={navIcon2} alt="Facebook" />
                        </a>
                        <a
                            href="https://github.com/Ushan2001"
                            target="_blank"
                            rel="noreferrer"
                            onClick={() =>
                                ReactGA.event({ category: "Social Click", action: "Clicked GitHub", label: "GitHub" })
                            }
                        >
                            <img src={github} alt="GitHub" />
                        </a>
                        <a
                            href="https://wa.me/94760673035"
                            target="_blank"
                            rel="noreferrer"
                            className="lnav-whatsapp"
                            onClick={() =>
                                ReactGA.event({ category: "Social Click", action: "Clicked WhatsApp", label: "WhatsApp" })
                            }
                        >
                            <svg viewBox="0 0 175.216 175.552" className="lnav-wa-icon">
                                <path d="M87.184 14.2A73.68 73.68 0 0 0 14.4 87.6a73.07 73.07 0 0 0 10.6 37.7L14 162.4l38.2-10.8a73.2 73.2 0 0 0 35 8.9 73.68 73.68 0 0 0 0-147.36zm0 134.8a61.09 61.09 0 0 1-31.2-8.5l-2.2-1.3-22.7 6.4 6-21.9-1.5-2.4a60.87 60.87 0 0 1-9.4-32.6A61.14 61.14 0 0 1 87.4 27.8a61.14 61.14 0 0 1-.2 121.2z" fill="currentColor" />
                                <path d="M127.4 103.5c-2.2-1.1-12.9-6.4-14.9-7.1s-3.5-1.1-4.9 1.1-5.7 7.1-6.9 8.5-2.5 1.6-4.7.5a59.07 59.07 0 0 1-17.4-10.7 65.08 65.08 0 0 1-12-15c-1.3-2.2-.1-3.3 1-4.4s2.2-2.5 3.3-3.8a14.7 14.7 0 0 0 2.2-3.6 4 4 0 0 0-.2-3.8c-.5-1.1-4.9-11.8-6.7-16.2s-3.5-3.7-4.9-3.7h-4.1a8 8 0 0 0-5.8 2.7 24.2 24.2 0 0 0-7.6 18c0 10.6 7.7 20.9 8.8 22.3s15.2 23.2 36.8 32.5a124.7 124.7 0 0 0 12.3 4.5 29.6 29.6 0 0 0 13.6.9c4.1-.6 12.9-5.3 14.7-10.4s1.8-9.5 1.3-10.4-1.8-1.5-4-2.5z" fill="currentColor" />
                            </svg>
                        </a>
                    </div>

                    <a href="/cv.pdf" download onClick={handleDownloadCV} className="lnav-cv-btn" title="Download CV and Cover Letter">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                    </a>
                </div>
            </nav>

            <DownloadConfirmationModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                onConfirm={handleConfirmDownload}
            />
        </>
    );
};
